import { Result, Button, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { FeatureFlag } from '@/config/featureFlags';
import { FEATURE_FLAG_META } from '@/config/featureFlags';
import { HOME_ROUTE } from '@/config/routes';
import { useFeatureFlags } from './FeatureFlagContext';

type Props = {
  flag: FeatureFlag;
  children: React.ReactNode;
  /** If true, show a soft empty state instead of null */
  showDisabled?: boolean;
};

export function FeatureGate({ flag, children, showDisabled = true }: Props) {
  const { isEnabled, setFlag } = useFeatureFlags();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  if (isEnabled(flag)) return <>{children}</>;
  if (!showDisabled) return null;
  const meta = FEATURE_FLAG_META[flag];
  const canToggleHere = import.meta.env.DEV;
  return (
    <Result
      status="info"
      title={t('featureGate.title')}
      subTitle={
        canToggleHere
          ? // Dev-only copy stays hardcoded English (DevFlagPanel idiom); prod copy is localized
            `${meta.label} (${flag}) ships dark and is currently off. Enable it here, or from the Feature flags panel.`
          : t('featureGate.notAvailable', { label: meta.label })
      }
      extra={
        <Space>
          {canToggleHere && (
            <Button type="primary" onClick={() => setFlag(flag, true)}>
              Enable this feature
            </Button>
          )}
          <Button
            type={canToggleHere ? 'default' : 'primary'}
            onClick={() => navigate(HOME_ROUTE)}
          >
            {t('featureGate.goToVendors')}
          </Button>
        </Space>
      }
    />
  );
}
